import {
  donorPasswordReset,
  getDonorTokenDetails,
} from "appState/auth/authSlice";
import LoginFrame from "assets/images/jpgs/loginframe.jpg";
import { TKIconEye, TKIconSpinner, TKIconViewEye } from "assets/images/svgs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import CompanyLogo from "shared/CompanyLogo";
import InputField from "shared/InputField";
import PasswordLength from "shared/PasswordLength";
import { resetPasswordValidationSchema } from "validation/signup/ResetPassword";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { isLoading, isAuthenticated, tokenDetails } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    isAuthenticated && navigate("/");
  }, [isAuthenticated, navigate]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDonorTokenDetails({ tokenId: token }));
  }, [dispatch, token]);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const calculateStrength = (value) => {
    let strength = 0;
    if (/\d/.test(value)) {
      strength++;
    }
    if (/[!@#$%^&*]/.test(value)) {
      strength++;
    }
    if (/[A-Z]/.test(value)) {
      strength++;
    }
    if (value.length >= 8) {
      strength++;
    }
    return strength;
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const formik = useFormik({
    initialValues: {
      userId: tokenDetails?.userId,
      token: token,
      password: "",
    },
    validationSchema: resetPasswordValidationSchema,
    onSubmit: (values) => {
      const body = {
        userId: tokenDetails?.userId,
        token: token,
        password: values.password,
      };
      dispatch(donorPasswordReset(body)).then((data) => {
        const { payload } = data;
        if (payload?.status === 200 && payload?.data === true) {
          let path = "/auth/donor/login";
          navigate(path);
        }
      });
    },
  });

  return (
    <div className="p-2">
      <div className="w-full h-full flex max-md:flex-wrap items-center gap-8">
        <div className="relative w-[60%] max-md:w-full max-md:h-[500px] h-screen">
          <img
            className="w-full h-full object-cover"
            src={LoginFrame}
            alt="TK-LoginFrame"
          />
          <p className="px-8 absolute bottom-10 max-md:text-[14px] text-[28px] text-white font-semibold shadow-md">
            Speak up for those who cannot speak for themselves, for the rights
            of all who are destitute. Speak up and judge fairly; defend the
            rights of the poor and needy.
          </p>
        </div>
        <div className="px-4 w-[40%] max-md:w-full flex flex-col justify-center items-start gap-4">
          <div className="mb-4">
            <CompanyLogo blue />
          </div>
          <div className="mb-2 w-full">
            <p className="text-[20px] font-bold mb-2">Reset Password</p>
            <div className="flex items-center justify-start gap-2">
              <NavLink to="/auth/donor/login">
                <p className="underline text-[#213874] font-medium hover:font-semibold">
                  Back to login
                </p>
              </NavLink>
            </div>
          </div>
          <form onSubmit={formik.handleSubmit} className="w-full">
            <div className="flex flex-col gap-4">
              <div className="relative flex flex-col gap-2">
                <label className="text-[14px] font-normal">Password</label>
                <InputField
                  className="border border-[#E6E6E6] rounded-md"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="************"
                  onFocus={() => setIsFocused(true)}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                />
                {formik.touched.password && formik.errors.password ? (
                  <p className="text-red-500 text-xs mt-[8px] w-[300px] md:w-[424px]">
                    {formik.errors.password}
                  </p>
                ) : null}
                {showPassword ? (
                  <TKIconViewEye
                    onClick={toggleShowPassword}
                    className="absolute right-4 top-[43px] z-2 hover:cursor-pointer"
                  />
                ) : (
                  <TKIconEye
                    onClick={toggleShowPassword}
                    className="absolute right-4 top-[43px] z-2 hover:cursor-pointer"
                  />
                )}
                <div className="flex justify-between mt-[10px]">
                  <PasswordLength
                    className={`strength-bar ${
                      isFocused ? "block" : "hidden"
                    } ${
                      calculateStrength(formik.values.password) === 1
                        ? "bg-red-500"
                        : calculateStrength(formik.values.password) === 2
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) === 3
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) >= 4
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  />
                  <PasswordLength
                    className={`strength-bar ${
                      isFocused ? "block" : "hidden"
                    } ${
                      calculateStrength(formik.values.password) === 2
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) === 3
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) >= 4
                        ? "bg-green-500"
                        : ""
                    }`}
                  />
                  <PasswordLength
                    className={`strength-bar ${
                      isFocused ? "block" : "hidden"
                    } ${
                      calculateStrength(formik.values.password) === 3
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) >= 4
                        ? "bg-green-500"
                        : ""
                    }`}
                  />
                  <PasswordLength
                    className={`strength-bar ${
                      isFocused ? "block" : "hidden"
                    } ${
                      calculateStrength(formik.values.password) >= 4
                        ? "bg-green-500"
                        : ""
                    }`}
                  />
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  className="w-full h-[50px] rounded-lg bg-[#213874] hover:bg-[#182d62] px-4 py-2 flex items-center justify-center"
                >
                  <p className="text-white font-semibold">
                    {isLoading ? <TKIconSpinner /> : "Reset Password"}
                  </p>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
